import { Link } from "react-router-dom";
import {
  Sparkles,
  Wand2,
  ShieldCheck,
  Video,
  BarChart3,
  Clock,
  Bell,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { LanguageSwitcher } from "@/components/language-switcher";
import { ThemeToggle } from "@/components/theme-toggle";
import { Testimonials } from "@/components/landing/testimonials";
import { useDict } from "@/lib/i18n/context";

const featureIcons = [Wand2, Video, ShieldCheck, Clock, BarChart3, Bell];

export function HomePage() {
  const t = useDict();
  const steps = t.landing.steps.map((s, i) => ({
    n: String(i + 1).padStart(2, "0"),
    ...s,
  }));
  return (
    <div className="flex min-h-screen min-w-0 flex-col bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl min-w-0 items-center justify-between gap-3 px-3 py-3 sm:gap-4 sm:px-4 sm:py-4">
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-600 text-white">
              <Sparkles className="h-5 w-5" />
            </span>
            <span className="truncate text-base font-bold tracking-wide sm:text-lg">
              {t.common.appName}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-2.5 sm:gap-3">
            <ThemeToggle />
            <LanguageSwitcher />
            <Link
              to="/careers"
              className="hidden text-sm font-medium text-muted hover:text-foreground md:inline"
            >
              {t.nav.careers}
            </Link>
            <Link
              to="/contact"
              className="hidden text-sm font-medium text-muted hover:text-foreground md:inline"
            >
              {t.nav.contact}
            </Link>
            <Link
              to="/sign-in"
              className="text-sm font-medium text-muted hover:text-foreground md:hidden"
            >
              {t.common.signIn}
            </Link>
            <Link to="/sign-in" className="hidden md:inline-flex">
              <Button variant="ghost" size="sm">
                {t.common.signIn}
              </Button>
            </Link>
            <Link to="/sign-up">
              <Button size="sm" className="px-3">
                {t.common.getStarted}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden bg-gradient-to-b from-brand-50 to-background dark:from-brand-950/50">
        <div className="mx-auto max-w-6xl px-4 py-14 text-center sm:py-20 md:py-28">
          <span className="inline-flex max-w-full items-center gap-2 rounded-full bg-card px-3 py-1.5 text-xs font-medium text-brand-700 shadow-sm sm:px-4 sm:text-sm dark:text-brand-300">
            <Sparkles className="h-4 w-4 shrink-0" />{" "}
            <span className="truncate">{t.landing.badge}</span>
          </span>
          <h1 className="mx-auto mt-6 max-w-3xl text-3xl font-extrabold leading-tight tracking-tight break-words sm:text-4xl md:text-6xl">
            {t.landing.heroTitle}{" "}
            <span className="text-brand-600">{t.landing.heroHighlight}</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base text-muted sm:text-lg">
            {t.landing.heroSubtitle}
          </p>
          <div className="mt-8 flex w-full flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
            <Link to="/sign-up" className="sm:inline-flex">
              <Button size="lg" className="w-full sm:w-auto">
                {t.landing.ctaStart}{" "}
                <ArrowRight className="h-4 w-4 rtl:rotate-180" />
              </Button>
            </Link>
            <Link to="/sign-in" className="sm:inline-flex">
              <Button size="lg" variant="outline" className="w-full sm:w-auto">
                {t.landing.ctaHaveAccount}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Testimonials
        title={t.landing.testimonialsTitle}
        items={t.landing.testimonials}
      />

      <section className="mx-auto max-w-6xl px-4 py-20">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold">{t.landing.featuresTitle}</h2>
          <p className="mt-3 text-muted">{t.landing.featuresSubtitle}</p>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {t.landing.features.map((f, i) => {
            const Icon = featureIcons[i];
            return (
              <div
                key={f.title}
                className="rounded-2xl border border-border bg-card p-6 shadow-sm transition-shadow hover:shadow-md"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-950 dark:text-brand-300">
                  <Icon className="h-6 w-6" />
                </span>
                <h3 className="mt-4 text-lg font-semibold">{f.title}</h3>
                <p className="mt-2 text-sm text-muted">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-card py-20">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold">{t.landing.howTitle}</h2>
            <p className="mt-3 text-muted">{t.landing.howSubtitle}</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {steps.map((s) => (
              <div key={s.n} className="rounded-2xl border border-border bg-background p-6">
                <span className="text-3xl font-extrabold text-brand-200 dark:text-brand-800">
                  {s.n}
                </span>
                <h3 className="mt-3 text-lg font-semibold">{s.title}</h3>
                <p className="mt-2 text-sm text-muted">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-6xl px-4 py-16 sm:py-20">
        <div className="rounded-3xl bg-brand-600 px-5 py-12 text-center text-white sm:px-8 sm:py-16">
          <h2 className="text-2xl font-bold sm:text-3xl">
            {t.landing.ctaBannerTitle}
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-brand-100">
            {t.landing.ctaBannerSubtitle}
          </p>
          <Link to="/sign-up">
            <Button size="lg" variant="secondary" className="mt-8">
              {t.landing.ctaStart} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-border py-8 text-center text-sm text-muted">
        <div className="mb-3 flex items-center justify-center gap-4">
          <Link to="/careers" className="hover:text-foreground">
            {t.nav.careers}
          </Link>
          <Link to="/contact" className="hover:text-foreground">
            {t.nav.contact}
          </Link>
        </div>
        © {new Date().getFullYear()} {t.common.appName}. {t.landing.footer}
      </footer>
    </div>
  );
}
